import React from 'react';
import { getLogoSrc } from '../utils/logoHelper';
import { formatGameDate } from '../utils/dateFormatter';
import api from '../services/api';

const API_BASE_URL = api.defaults.baseURL.replace('/api', '');
const WIDTH = 1080;
const HEIGHT = 1080;

function GraphicRecapHighlight({
  graphicData,
  teamSport,
  teamColors,
  backgroundStyle = 'linear-gradient',
  homeTeamLogo,
  awayTeamLogoPreview,
  showWatermark = true,
  dominantImage = null,
  heroFont = 'Bebas Neue',
  heroFontSize = null,
  accentBlockColor = null,
  detailTextColor = null,
  dominantImageX = null,
  dominantImageY = null,
  dominantImageScale = null,
  dominantImageOpacity = 100,
  dateFormat = 'full',
}) {
  const {
    homeTeam,
    homeScore,
    awayTeam,
    awayScore,
    gameDate,
    topPerformer,
    topPerformerStats,
  } = graphicData;

  const homeWon = parseInt(homeScore) > parseInt(awayScore);
  const isTie = parseInt(homeScore) === parseInt(awayScore);
  const resultLabel = isTie ? 'TIE GAME' : homeWon ? 'VICTORY' : 'FINAL';

  const backgroundPhoto = graphicData.backgroundPhoto || null;
  const bgImageScale = (graphicData.bgImageScale ?? 100) / 100;
  const bgImageX = graphicData.bgImageX ?? 50;
  const bgImageY = graphicData.bgImageY ?? 50;
  const heroFontFamily = `'${heroFont}', 'Arial Narrow', Arial, sans-serif`;
  const barlowFamily = "'Barlow Condensed', 'Barlow', 'Arial Narrow', Arial, sans-serif";
  const accentColor = accentBlockColor || teamColors.secondary;
  const scale = (dominantImageScale ?? 100) / 100;
  const resultFontSize = heroFontSize ? Number(heroFontSize) : 160;

  const getBackground = () => {
    switch (backgroundStyle) {
      case 'linear-gradient':
        return `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'radial-gradient':
        return `radial-gradient(circle at center, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'diagonal-gradient':
        return `linear-gradient(45deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'split-two-tone':
        return `linear-gradient(90deg, ${teamColors.primary} 50%, ${teamColors.secondary} 50%)`;
      case 'solid-primary':
        return teamColors.primary;
      case 'solid-secondary':
        return teamColors.secondary;
      default:
        return `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
    }
  };

  return (
    <div
      id="graphic-recap"
      style={{
        position: 'relative',
        width: WIDTH,
        height: HEIGHT,
        overflow: 'hidden',
        borderRadius: 16,
        background: getBackground(),
        fontFamily: heroFontFamily,
      }}
    >
      {/* Layer 1: Background photo */}
      {backgroundPhoto && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: WIDTH,
            height: HEIGHT,
            overflow: 'hidden',
            zIndex: 1,
          }}
        >
          <img
            src={backgroundPhoto}
            alt=""
            crossOrigin="anonymous"
            style={{
              position: 'absolute',
              top: `${bgImageY}%`,
              left: `${bgImageX}%`,
              transform: `translate(-50%, -50%) scale(${bgImageScale})`,
              transformOrigin: 'center center',
              width: 'auto',
              height: 'auto',
              minWidth: '100%',
              minHeight: '100%',
              maxWidth: 'none',
            }}
          />
        </div>
      )}

      {/* Layer 2a: Dominant image */}
      {dominantImage && (
        <img
          src={dominantImage}
          alt=""
          crossOrigin="anonymous"
          style={{
            position: 'absolute',
            top: `${dominantImageY ?? 50}%`,
            left: `${dominantImageX ?? 50}%`,
            transform: `translate(-50%, -50%) scale(${scale})`,
            opacity: (dominantImageOpacity ?? 100) / 100,
            transformOrigin: 'center center',
            height: 'auto',
            width: '100%',
            maxWidth: 'none',
            overflow: 'visible',
            zIndex: 2,
          }}
        />
      )}

      {/* Layer 2b: Diagonal accent block (no dominant image) */}
      {!dominantImage && (
        <div
          style={{
            position: 'absolute',
            bottom: -320,
            right: -320,
            width: 960,
            height: 960,
            background: accentColor,
            transform: 'rotate(25deg)',
            zIndex: 2,
          }}
        />
      )}

      {/* Layer 3: Gradient scrim — left to right */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: WIDTH,
          height: HEIGHT,
          background:
            'linear-gradient(to right, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.5) 60%, rgba(0,0,0,0.1) 100%)',
          zIndex: 3,
        }}
      />

      {/* Layer 4: Left accent bar */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 14,
          height: HEIGHT,
          background: teamColors.primary,
          zIndex: 4,
        }}
      />

      {/* Layer 5: Top broadcast bar */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 14,
          width: WIDTH - 14,
          height: 110,
          background: 'rgba(0,0,0,0.75)',
          zIndex: 5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 24,
        }}
      >
        {homeTeamLogo && (
          <img
            src={getLogoSrc(homeTeamLogo, API_BASE_URL)}
            alt={homeTeam || 'Home'}
            crossOrigin="anonymous"
            style={{ height: 56, width: 'auto' }}
          />
        )}
        <div
          style={{
            fontFamily: heroFontFamily,
            fontSize: 72,
            color: '#ffffff',
            lineHeight: 1,
            letterSpacing: 4,
          }}
        >
          {homeScore || '0'}
        </div>
        <div
          style={{
            fontFamily: heroFontFamily,
            fontSize: 48,
            color: 'rgba(255,255,255,0.4)',
            lineHeight: 1,
          }}
        >
          -
        </div>
        <div
          style={{
            fontFamily: heroFontFamily,
            fontSize: 72,
            color: 'rgba(255,255,255,0.6)',
            lineHeight: 1,
            letterSpacing: 4,
          }}
        >
          {awayScore || '0'}
        </div>
        {awayTeamLogoPreview && (
          <img
            src={awayTeamLogoPreview}
            alt={awayTeam || 'Away'}
            crossOrigin="anonymous"
            style={{ height: 56, width: 'auto' }}
          />
        )}
      </div>

      {/* Layer 6: Result label */}
      <div
        style={{
          position: 'absolute',
          top: 140,
          left: 60,
          zIndex: 6,
          fontFamily: heroFontFamily,
          fontSize: resultFontSize,
          color: accentColor,
          letterSpacing: 6,
          lineHeight: 1,
        }}
      >
        {resultLabel}
      </div>

      {/* Layer 7: Player spotlight */}
      <div
        style={{
          position: 'absolute',
          top: 360,
          left: 60,
          right: 560,
          zIndex: 7,
        }}
      >
        {topPerformer ? (
          <>
            <div
              style={{
                fontFamily: barlowFamily,
                fontSize: 30,
                color: accentColor,
                letterSpacing: 8,
                lineHeight: 1,
                marginBottom: 12,
              }}
            >
              PLAYER OF THE GAME
            </div>
            <div
              style={{
                fontFamily: heroFontFamily,
                fontSize: 110,
                color: '#ffffff',
                lineHeight: 0.9,
              }}
            >
              {topPerformer}
            </div>
            {topPerformerStats && (
              <div
                style={{
                  fontFamily: barlowFamily,
                  fontSize: 52,
                  color: detailTextColor || 'rgba(255,255,255,0.8)',
                  lineHeight: 1,
                  marginTop: 20,
                }}
              >
                {topPerformerStats}
              </div>
            )}
          </>
        ) : (
          <>
            <div
              style={{
                fontFamily: heroFontFamily,
                fontSize: 80,
                color: '#ffffff',
                lineHeight: 1,
                marginBottom: 8,
              }}
            >
              {homeTeam || 'YOUR TEAM'}
            </div>
            <div
              style={{
                fontFamily: heroFontFamily,
                fontSize: 48,
                color: accentColor,
                lineHeight: 1,
                marginBottom: 8,
              }}
            >
              VS
            </div>
            <div
              style={{
                fontFamily: heroFontFamily,
                fontSize: 64,
                color: 'rgba(255,255,255,0.7)',
                lineHeight: 1,
              }}
            >
              {awayTeam || 'OPPONENT'}
            </div>
          </>
        )}
      </div>

      {/* Layer 8: Bottom info strip */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: WIDTH,
          height: 80,
          background: 'rgba(0,0,0,0.6)',
          zIndex: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {gameDate && (
          <div
            style={{
              fontFamily: barlowFamily,
              fontSize: 36,
              color: 'rgba(255,255,255,0.6)',
              letterSpacing: 3,
              lineHeight: 1,
            }}
          >
            {formatGameDate(gameDate, dateFormat)}
          </div>
        )}
      </div>

      {/* Layer 9: Watermark */}
      {showWatermark && (
        <div
          style={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            zIndex: 9,
            background: 'rgba(255,255,255,0.92)',
            padding: '6px 12px',
            borderRadius: 8,
            pointerEvents: 'none',
          }}
        >
          <img src="/PMlogoleft.png" alt="Playmaker Media Kit" style={{ height: 80, width: 'auto', display: 'block' }} />
        </div>
      )}
    </div>
  );
}

export default GraphicRecapHighlight;
